import * as React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import MuiDrawer from '@mui/material/Drawer';
import { useAtom } from 'jotai';
import { useAtomValue } from 'jotai';
import { userPermissionsAtom } from 'atoms/permissions';

import { isDrawerOpen as isDrawerOpenAtom } from 'atoms/drawer';
import { drawerWidth } from 'utils/constants';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import { hasPermissions } from 'utils/permissions';

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

interface MenuItem {
  id: string;
  name: string;
  link?: string;
  icon?: string;
  subMenu?: MenuItem[];
  requiredPermissions?: string[];
}

type MenuItemWithAccessControl = MenuItem & {
  isVisible: (userRoles: string[]) => boolean;
};

const withRolesCheck = (menuItem: MenuItem, roles: string[]): MenuItemWithAccessControl => ({
  ...menuItem,
  isVisible(userRoles: string[]) {
    return roles.every(role => userRoles.includes(role));
  },
});

const withAllowAny = (menuItem: MenuItem): MenuItemWithAccessControl => ({
  ...menuItem,
  isVisible(_: string[]) {
    return true;
  },
});

const menuItems: MenuItemWithAccessControl[] = [
  withAllowAny({
    id: '1',
    name: 'Πίνακας Ελέγχου',
    link: '/',
    icon: 'HomeOutlined',
  }),
  withAllowAny({
    id: '2',
    name: 'Επαφές',
    link: `/contacts`,
    icon: 'BookOutlined',
  }),
  withAllowAny({
    id: '3',
    name: 'Εταιρείες',
    link: `/companies`,
    icon: 'BusinessOutlined',
    requiredPermissions: ['companies:read'],
  }),
  withAllowAny({
    id: '4',
    name: 'Παραγγελίες',
    link: `/orders`,
    icon: 'ReceiptLongOutlined',
  }),
  withAllowAny({
    id: '5',
    name: 'Υπάλληλοι',
    link: `/employees`,
    icon: 'BadgeOutlined',
  }),
  withAllowAny({
    id: '6',
    name: 'Ημερολόγιο',
    link: `/bookings`,
    icon: 'CalendarMonthOutlined',
  }),
  withAllowAny({
    id: '7',
    name: 'Υπηρεσίες',
    link: `/services`,
    icon: 'CategoryOutlined',
  }),
  withAllowAny({
    id: '8',
    name: 'Ρυθμίσεις',
    link: `/settings`,
    icon: 'SettingsOutlined',
  }),
];

const Sidebar = () => {
  const [openMenu, setOpenMenu] = React.useState<{ [key: string]: boolean }>({});
  const [isDrawerOpen] = useAtom(isDrawerOpenAtom);
  const permissions = useAtomValue(userPermissionsAtom);

  const navigate = useNavigate();
  const location = useLocation();

  const handleToggleMenuItem = (id: string) => {
    setOpenMenu(prevOpenMenu => ({
      ...prevOpenMenu,
      [id]: !prevOpenMenu[id],
    }));
  };

  const handleNavigation = (link?: string) => {
    if (link) {
      navigate(link);
    }
  };

  return (
    <Drawer variant="permanent" open={isDrawerOpen}>
      <Toolbar />
      <Box>
        <List>
          {menuItems
            .filter(item => hasPermissions(permissions, item.requiredPermissions))
            .flatMap(item => {
              const IconComponent = Icons[item.icon as keyof typeof Icons];
              const isActive = location.pathname.substring(1).split('/')[0] === item.link?.substring(1);

              return (
                <Box key={item.id}>
                  <Tooltip
                    title={item.name}
                    placement="right"
                    slots={{
                      transition: Zoom,
                    }}
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '1em',
                          p: 1,
                        },
                      },
                    }}
                    arrow
                  >
                    <ListItem disablePadding>
                      <ListItemButton
                        disableRipple
                        selected={isActive}
                        onClick={() => {
                          handleToggleMenuItem(item.id);
                          handleNavigation(item.link);
                        }}
                      >
                        <ListItemIcon>
                          {IconComponent && <IconComponent color={isActive ? 'secondary' : 'inherit'} />}
                        </ListItemIcon>
                        <ListItemText primary={item.name} />
                        {item.subMenu ? openMenu[item.id] ? <ExpandLess /> : <ExpandMore /> : null}
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                  {item.subMenu && (
                    <Collapse in={openMenu[item.id]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.subMenu
                          .filter(item => hasPermissions(permissions, item.requiredPermissions))
                          .map(subItem => (
                          <ListItem disablePadding key={subItem.id}>
                            <ListItemButton
                              disableRipple
                              sx={{ pl: 9 }}
                              onClick={() => {
                                handleNavigation(subItem.link);
                              }}
                            >
                              <ListItemText primary={subItem.name} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
